import { DragEndEvent } from '@dnd-kit/core'
import { differenceInHours } from 'date-fns'
import { useCallback, useMemo, useRef } from 'react'

import { ReplayTimeRangeProps } from './ReplayTimeRange.types'
import {
  buildTimelineMarkers,
  computeDraggedEndTime,
  computeDraggedRange,
  computeDraggedStartTime,
} from './ReplayTimeRange.utils'

export const useReplayTimeRange = ({
  rangeStartTime,
  rangeEndTime,
  selectionStartTime,
  selectionEndTime,
  setSelectionStartTime,
  setSelectionEndTime,
}: Omit<ReplayTimeRangeProps, 'markerTracks'>) => {
  const rangeElementRef = useRef<HTMLDivElement>(null)

  const periodInSec = useMemo(
    () => differenceInHours(rangeEndTime, rangeStartTime, { roundingMethod: 'ceil' }) * 60 * 60,
    [rangeStartTime, rangeEndTime],
  )

  const markers = useMemo(
    () => buildTimelineMarkers(rangeStartTime, rangeEndTime),
    [rangeStartTime, rangeEndTime],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!rangeElementRef.current) return
      const deltaPercent = event.delta.x / rangeElementRef.current.getBoundingClientRect().width
      const times = {
        selectionStartTime,
        selectionEndTime,
        rangeStartTime,
        rangeEndTime,
        periodInSec,
        deltaPercent,
      }

      if (event.active.id === 'start-range-dragger') {
        setSelectionStartTime(computeDraggedStartTime(times))
      }
      if (event.active.id === 'end-range-dragger') {
        setSelectionEndTime(computeDraggedEndTime(times))
      }
      if (event.active.id === 'complete-range-dragger') {
        const { start, end } = computeDraggedRange(times)
        setSelectionStartTime(start)
        setSelectionEndTime(end)
      }
    },
    [
      selectionStartTime,
      selectionEndTime,
      rangeStartTime,
      rangeEndTime,
      periodInSec,
      setSelectionStartTime,
      setSelectionEndTime,
    ],
  )

  return { rangeElementRef, periodInSec, markers, handleDragEnd }
}
